// src/pages/Appli/AppliAdmin/ListingClients/hooks/useAssociationFilters.js

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  ASSOCIATION_PRODUCTS,
} from "../data/associations.data";

import {
  compareAssociationStatuses,
  getAssociationGroup,
  getAssociationSearchText,
  getReminderState,
  isReminderDue,
  normalizeSearchText,
  splitDisciplines,
} from "../utils/association.utils";

const VIEW_MODE_STORAGE_KEY =
  "monappli-associations-view-mode";

const VALID_VIEW_MODES = [
  "auto",
  "table",
  "cards",
];

export const DEFAULT_ASSOCIATION_FILTERS = {
  search: "",
  status: "",
  product: "",
  discipline: "",
  favorite: "all",
  reminder: "all",
  sortBy: "status",
};

function compareNames(first, second) {
  return String(first.nom_asso || "").localeCompare(
    String(second.nom_asso || ""),
    "fr",
  );
}

function matchesReminderFilter(
  association,
  reminderFilter,
) {
  if (reminderFilter === "all") return true;

  const state = getReminderState(
    association.date_relance_asso,
  );

  if (reminderFilter === "due") {
    return ["overdue", "today"].includes(
      state.key,
    );
  }

  return state.key === reminderFilter;
}

function sortAssociations(associations, sortBy) {
  return [...associations].sort((first, second) => {
    if (sortBy === "name") {
      return compareNames(first, second);
    }

    if (sortBy === "product") {
      const productComparison = String(
        first.produit_asso || "",
      ).localeCompare(
        String(second.produit_asso || ""),
        "fr",
      );

      return productComparison || compareNames(first, second);
    }

    if (sortBy === "reminder") {
      const firstDate =
        first.date_relance_asso || "9999-12-31";

      const secondDate =
        second.date_relance_asso || "9999-12-31";

      const dateComparison =
        firstDate.localeCompare(secondDate);

      return dateComparison || compareNames(first, second);
    }

    if (sortBy === "recent") {
      const firstDate = new Date(
        first.created_at || 0,
      ).getTime();

      const secondDate = new Date(
        second.created_at || 0,
      ).getTime();

      return (
        secondDate -
          firstDate ||
        compareNames(first, second)
      );
    }

    return compareAssociationStatuses(
      first,
      second,
    );
  });
}

function getInitialViewMode() {
  if (typeof window === "undefined") {
    return "auto";
  }

  const storedValue = localStorage.getItem(
    VIEW_MODE_STORAGE_KEY,
  );

  return VALID_VIEW_MODES.includes(storedValue)
    ? storedValue
    : "auto";
}

export default function useAssociationFilters(
  associations = [],
) {
  const [filters, setFilters] = useState(
    DEFAULT_ASSOCIATION_FILTERS,
  );

  const [viewMode, setViewModeState] = useState(
    getInitialViewMode,
  );

  useEffect(() => {
    localStorage.setItem(
      VIEW_MODE_STORAGE_KEY,
      viewMode,
    );
  }, [viewMode]);

  const setFilter = useCallback((name, value) => {
    setFilters((current) => ({
      ...current,
      [name]: value,
    }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_ASSOCIATION_FILTERS);
  }, []);

  const setViewMode = useCallback((mode) => {
    if (VALID_VIEW_MODES.includes(mode)) {
      setViewModeState(mode);
    }
  }, []);

  const disciplineOptions = useMemo(() => {
    const disciplines = new Set();

    associations.forEach((association) => {
      splitDisciplines(
        association.discipline_asso,
      ).forEach((discipline) => {
        disciplines.add(discipline);
      });
    });

    return [...disciplines].sort((first, second) =>
      first.localeCompare(second, "fr"),
    );
  }, [associations]);

  const productOptions = useMemo(() => {
    const knownProducts = new Set(
      ASSOCIATION_PRODUCTS,
    );

    const additionalProducts = associations
      .map(
        (association) =>
          association.produit_asso?.trim(),
      )
      .filter(Boolean)
      .filter(
        (product) => !knownProducts.has(product),
      )
      .filter(
        (product, index, array) =>
          array.indexOf(product) === index,
      )
      .sort((first, second) =>
        first.localeCompare(second, "fr"),
      );

    return [
      ...ASSOCIATION_PRODUCTS,
      ...additionalProducts,
    ];
  }, [associations]);

  const filteredAssociations = useMemo(() => {
    const search = normalizeSearchText(
      filters.search,
    );

    const selectedDiscipline =
      normalizeSearchText(filters.discipline);

    const filtered = associations.filter(
      (association) => {
        if (
          search &&
          !getAssociationSearchText(
            association,
          ).includes(search)
        ) {
          return false;
        }

        if (
          filters.status &&
          association.statut_asso !==
            filters.status
        ) {
          return false;
        }

        if (
          filters.product &&
          association.produit_asso !==
            filters.product
        ) {
          return false;
        }

        if (selectedDiscipline) {
          const disciplines = splitDisciplines(
            association.discipline_asso,
          ).map(normalizeSearchText);

          if (
            !disciplines.includes(
              selectedDiscipline,
            )
          ) {
            return false;
          }
        }

        if (
          filters.favorite === "yes" &&
          !association.is_favorite
        ) {
          return false;
        }

        if (
          filters.favorite === "no" &&
          association.is_favorite
        ) {
          return false;
        }

        return matchesReminderFilter(
          association,
          filters.reminder,
        );
      },
    );

    return sortAssociations(
      filtered,
      filters.sortBy,
    );
  }, [associations, filters]);

  const kpis = useMemo(() => {
    const result = {
      total: filteredAssociations.length,
      prospects: 0,
      tests: 0,
      clients: 0,
      remindersDue: 0,
      favorites: 0,
    };

    filteredAssociations.forEach(
      (association) => {
        const group =
          getAssociationGroup(association);

        if (group === "prospect") {
          result.prospects += 1;
        }

        if (group === "test") {
          result.tests += 1;
        }

        if (group === "client") {
          result.clients += 1;
        }

        if (
          isReminderDue(
            association.date_relance_asso,
          )
        ) {
          result.remindersDue += 1;
        }

        if (association.is_favorite) {
          result.favorites += 1;
        }
      },
    );

    return result;
  }, [filteredAssociations]);

  const activeFilterCount = useMemo(() => {
    return [
      filters.search,
      filters.status,
      filters.product,
      filters.discipline,
      filters.favorite !== "all"
        ? filters.favorite
        : "",
      filters.reminder !== "all"
        ? filters.reminder
        : "",
    ].filter(Boolean).length;
  }, [filters]);

  return {
    filters,
    setFilter,
    resetFilters,

    viewMode,
    setViewMode,

    filteredAssociations,
    disciplineOptions,
    productOptions,

    kpis,
    activeFilterCount,
    hasActiveFilters: activeFilterCount > 0,
  };
}